/**
 * ItemGo Service Delivery — Pricing Engine
 * All values in pence (GBP). VAT applies to platform fee only.
 * Everything configurable from Firestore pluginConfigs/{serviceId}.
 */
export const DEFAULT_DELIVERY_PRICING_CONFIG = {
    /** Platform booking fee (£5.99) */
    serviceFee: 599,
    /** VAT rate (20 = 20%) */
    vatRate: 20,
    /** Base price for any delivery (£35.00 — covers included miles) */
    basePrice: 3500,
    /** Miles included in the base price */
    includedMiles: 20,
    /** Per extra mile beyond included miles (£1.50) */
    pricePerExtraMile: 150,
    /** Per additional stop beyond the first 2 (£2.00) */
    pricePerStop: 200,
    /** Per kg of item weight (£0.10) */
    pricePerKg: 10,
    /** Per m³ of item volume (£0.50) */
    pricePerM3: 50,
    /** Minimum charged distance */
    minDistance: 1,
    /** Express surcharge multiplier */
    expressMultiplier: 1.5,
    /** Bundle matching enabled */
    bundleEnabled: true,
    /** Max jobs per bundled route */
    bundleMaxJobs: 5,
    /** Max deviation miles for bundle matching */
    bundleMaxDeviationMiles: 5,
    /** Progressive discounts per bundle position (2nd, 3rd, 4th, 5th job) */
    bundleDiscounts: [5, 10, 15, 20],
};
export function calculateDeliveryPricing(input) {
    const config = input.config;
    const effectiveDistance = Math.max(input.distanceMiles, config.minDistance);
    const basePrice = config.basePrice;
    const extraMiles = Math.max(0, effectiveDistance - (config.includedMiles ?? 20));
    const distanceCharge = Math.round(extraMiles * config.pricePerExtraMile);
    const stopCharge = Math.max(0, (input.stops - 2) * config.pricePerStop);
    const weightCharge = Math.round(input.totalWeightKg * config.pricePerKg);
    const volumeCharge = Math.round((input.totalVolumeM3 ?? 0) * (config.pricePerM3 ?? 50));
    // Cargo charge: use the GREATER of weight or volume, never both
    const cargoCharge = Math.max(weightCharge, volumeCharge);
    const speedMultiplier = input.speed === "express" ? config.expressMultiplier : 1.0;
    // Apply bundle discount if this job is bundled
    const bundlePosition = input.bundlePosition ?? 0;
    // Parse bundle discounts: string "5,10,15,20" from admin or array [5,10,15,20]
    let bundleDiscounts;
    if (Array.isArray(config.bundleDiscounts)) {
        bundleDiscounts = config.bundleDiscounts;
    }
    else if (typeof config.bundleDiscounts === "string" && config.bundleDiscounts.trim()) {
        bundleDiscounts = config.bundleDiscounts
            .split(",")
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => !isNaN(n));
    }
    else {
        bundleDiscounts = [5, 10, 15, 20];
    }
    let bundleDiscountPercent = 0;
    let bundleDiscount = 0;
    // Check for explicit overrides first (backload pricing, admin-set discounts)
    if (input.discountPercentOverride !== undefined && input.discountPercentOverride > 0) {
        bundleDiscountPercent = input.discountPercentOverride;
    }
    else if (input.discountFixedPenceOverride !== undefined && input.discountFixedPenceOverride > 0) {
        // Fixed pence override — skip percentage calculation below
        bundleDiscount = input.discountFixedPenceOverride;
    }
    else if (bundlePosition >= 2) {
        const idx = bundlePosition - 2; // 2nd job = index 0
        bundleDiscountPercent = idx < bundleDiscounts.length ? bundleDiscounts[idx] : 0;
    }
    // Driver/service subtotal (excluding platform fee and VAT)
    // Use cargoCharge (max of weight/volume) instead of summing both
    const subtotal = basePrice + distanceCharge + stopCharge + cargoCharge;
    const servicePriceBeforeDiscount = Math.round(subtotal * speedMultiplier);
    // If we didn't set bundleDiscount via fixed pence override, calculate from percentage
    if (bundleDiscount === 0 && bundleDiscountPercent > 0) {
        bundleDiscount = Math.round(servicePriceBeforeDiscount * bundleDiscountPercent / 100);
    }
    const servicePrice = servicePriceBeforeDiscount - bundleDiscount;
    // Platform fee (booking fee) — this is what VAT is charged on
    const serviceFee = config.serviceFee ?? 500;
    const vatRate = config.vatRate ?? 0.20;
    // Normalize: if stored as percentage integer (e.g. 20 for 20%), convert to decimal
    const normalizedVatRate = vatRate > 1 ? vatRate / 100 : vatRate;
    // VAT on platform fee ONLY, not on the full service price
    const vatAmount = Math.round((servicePrice + serviceFee) * normalizedVatRate);
    // VAT breakdown for display
    const vatRatePercent = Math.round(normalizedVatRate * 100);
    // Driver earnings = service price (pass-through, no VAT)
    const driverEarnings = servicePrice;
    // Platform revenue = service fee (subject to VAT)
    const platformRevenue = serviceFee;
    // Total customer pays = service price + platform fee + VAT on fee
    const customerTotal = servicePrice + serviceFee + vatAmount;
    return {
        basePrice,
        distanceCharge,
        stopCharge,
        weightCharge,
        volumeCharge,
        cargoCharge, // max(weightCharge, volumeCharge) — only one applies
        speedMultiplier,
        bundleDiscount,
        bundleDiscountPercent,
        servicePrice,
        serviceFee,
        vatAmount,
        vatRatePercent,
        driverEarnings,
        platformRevenue,
        customerTotal,
    };
}
export function formatPence(pence) {
    return `£${(pence / 100).toFixed(2)}`;
}
//# sourceMappingURL=pricing.js.map